import React, { useEffect, useState } from 'react';

import { Box, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

import type { LensCardType, LensSortConfig, LensSortKey } from '~/types/leLens';

import { cardShowsSort, sortKeys } from './useStyleTabState';

// Lifted verbatim from LensCardDialog.tsx's Data tab (the `dataOrderControls`
// block): same three controls (Order rows, Direction, Sort on), same copy.
// It renders on the panel's Data tab, below the where chips (02_RIGHT_PANEL_SPEC
// does not name it directly; it is the legacy dialog's row-order section,
// which config.sort still needs, per the orchestrator's note that the panel's
// Data tab had no equivalent control yet).

export type RowOrderProps = {
  /** Re-seeds the three controls when a different card opens, the same guard
   *  every other tab's state uses. */
  cardId: string;
  type: LensCardType;
  /** The card's current row order, if any (config.sort). */
  sort?: LensSortConfig;
  /** Series from the last render, for "Sort on". */
  seriesNames: Array<string>;
  busy?: boolean;
  /** Fired with the next config.sort (or undefined for "As the query
   *  returned it") on every change, the live-preview hand-off. */
  onChange?: (sort: LensSortConfig | undefined) => void;
};

/**
 * The panel's row-order controls. A card type the shared axis-chart model or
 * the table does not reorder (a metric, a pie, a heatmap, a box plot, an
 * insight) renders nothing, matching `cardShowsSort`.
 */
export default function RowOrder({ busy = false, cardId, onChange, seriesNames, sort, type }: RowOrderProps) {
  const [sortBy, setSortBy] = useState<LensSortKey>(sort?.by ?? 'none');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(sort?.direction ?? 'desc');
  const [sortSeries, setSortSeries] = useState(sort?.series ?? '');

  // Re-seed only when a different card opens: re-seeding on every render
  // would drop an edit in progress, the same trap every other tab avoids.
  useEffect(() => {
    setSortBy(sort?.by ?? 'none');
    setSortDirection(sort?.direction ?? 'desc');
    setSortSeries(sort?.series ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId]);

  const nextSort: LensSortConfig | undefined =
    sortBy === 'none'
      ? undefined
      : { by: sortBy, direction: sortDirection, ...(sortBy === 'value' && sortSeries ? { series: sortSeries } : {}) };
  const sortKey = JSON.stringify(nextSort);
  useEffect(() => {
    onChange?.(nextSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey]);

  if (!cardShowsSort(type)) {
    return null;
  }

  return (
    <Box>
      {/* The section header above already says "Sort": a repeated heading here
          would say the same thing twice. Only the one guidance line stays,
          since it states a fact (a query limit still governs which rows this
          can reorder) rather than restating the section's name. */}
      <Typography color="text.secondary" sx={{ display: 'block', mb: 1.5 }} variant="caption">
        Choose the order used to draw rows. Change the query when its own limit needs to return different rows.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="le-lens-sort-by">Order rows</InputLabel>
          <Select
            disabled={busy}
            label="Order rows"
            labelId="le-lens-sort-by"
            onChange={(event) => setSortBy(event.target.value as LensSortKey)}
            value={sortBy}
          >
            {sortKeys.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {sortBy !== 'none' && (
          <FormControl fullWidth>
            <InputLabel id="le-lens-sort-direction">Direction</InputLabel>
            <Select
              disabled={busy}
              label="Direction"
              labelId="le-lens-sort-direction"
              onChange={(event) => setSortDirection(event.target.value as 'asc' | 'desc')}
              value={sortDirection}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>
      {sortBy === 'value' && seriesNames.length > 1 && (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="le-lens-sort-series">Sort on</InputLabel>
          <Select
            disabled={busy}
            label="Sort on"
            labelId="le-lens-sort-series"
            onChange={(event) => setSortSeries(event.target.value)}
            value={sortSeries}
          >
            <MenuItem value="">Total across all series</MenuItem>
            {seriesNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {sortBy !== 'none' && (
        <Typography color="text.secondary" sx={{ display: 'block', mt: 1.5 }} variant="caption">
          Ordering changes rows this query already returned. If the SQL has a limit, change the query so the right rows
          arrive first.
        </Typography>
      )}
    </Box>
  );
}
